import { Table } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";
import { CalendarOutlined, RollbackOutlined } from "@ant-design/icons";

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "billno", label: "Inv No" },
  { key: "CustName", label: "Customer" },
  { key: "TOTQTY", label: "Qty", badge: "pieces" },
  { key: "totamt", label: "Total Amt", badge: "netwt" },
];
const secondaryFields = [
  { key: "BillDate", label: "Inv Date" },
  { key: "PhoneNo", label: "Mobile" },
  { key: "TOTBASICAMT", label: "Basic Amt" },
  { key: "TOTGST", label: "GST" },
];

const ReturnRegister = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [openingCash, setOpeningCash] = useState(0);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [tableVisible, setTableVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ── fetch return register for the selected date range ── */
  useEffect(() => {
    if (dates[0] && dates[1]) {
      const fromDate = dates[0].format("YYYY-MM-DD");
      const toDate = dates[1].format("YYYY-MM-DD");

      setLoading(true);
      setTableVisible(false);

      axios
        .get(
          `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=SALE_RETURN_CREDIT_BILLMAST&where=BILLDATE>='${fromDate}' AND BILLDATE<='${toDate}'&order=BILLNO DESC`,
          {
            headers: {
              tenantName: tenantName,
            },
          },
        )
        .then((response) => {
          const detailsData = response.data;
          let balance = openingCash;
          const calculatedData = detailsData.map((item, index) => {
            balance = balance + item.DEBIT - item.CREDIT;
            return {
              ...item,
              BALANCE: balance,
              key: index + 1,
              serialNo: index + 1,
            };
          });
          setFilteredData(calculatedData);
          setTimeout(() => setTableVisible(true), 80);
        })
        .catch((error) => {
          console.error("Error fetching cash book details:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [dates, openingCash]);

  const columns = [
    {
      title: "S.No",
      key: "serialNo",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      className: "col-sno",
    },
    {
      title: "Inv Date",
      dataIndex: "BillDate",
      key: "BillDate",
      width: 110,
      align: "center",
      className: "col-date",
    },
    {
      title: "Inv No",
      dataIndex: "billno",
      key: "billno",
      align: "center",
      className: "col-tagno",
      render: (value) => (
        <span className="tagBadge">{Number(value || 0).toFixed(0)}</span>
      ),
    },
    {
      title: "Customer Name",
      dataIndex: "CustName",
      key: "CustName",
      align: "left",
      width: 260,
      className: "col-dealername",
    },
    {
      title: "Mobile Number",
      dataIndex: "PhoneNo",
      key: "PhoneNo",
      align: "left",
      width: 160,
    },
    {
      title: "QTY",
      dataIndex: "TOTQTY",
      key: "TOTQTY",
      align: "center",
      className: "col-pieces",
      render: (_, record) => (
        <span className="piecesBadge">
          {record?.totqty ?? record?.TOTQTY ?? 0}
        </span>
      ),
    },
    {
      title: "Basic Amount",
      dataIndex: "TOTBASICAMT",
      key: "TOTBASICAMT",
      align: "right",
      render: (value) => <span>{Number(value || 0).toFixed(2)}</span>,
    },
    {
      title: "GST",
      key: "TINNO",
      dataIndex: "TINNO",
      align: "right",
      render: (_, record) => <span>{Number(record.TOTGST || 0).toFixed(2)}</span>,
    },
    {
      title: "CGST",
      key: "TOTCGSTAMT",
      dataIndex: "TOTCGSTAMT",
      align: "right",
      render: (_, record) => (
        <span>{Number(record.TOTCGSTAMT || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "SGST",
      key: "TOTSGSTAMT",
      dataIndex: "TOTSGSTAMT",
      align: "right",
      render: (_, record) => (
        <span>{Number(record.TOTSGSTAMT || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "IGST",
      key: "TOTIGSTAMT",
      dataIndex: "TOTIGSTAMT",
      align: "right",
      render: (_, record) => (
        <span>{Number(record.TOTIGSTAMT || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totamt",
      key: "totamt",
      align: "right",
      className: "col-netwt",
      render: (value) => <span className="wtBadge">{value}</span>,
    },
  ];

  /* ── totals ── */
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        acc.qty += Number(item?.totqty ?? item?.TOTQTY ?? 0);
        acc.basic += Number(item.TOTBASICAMT || 0);
        acc.amount += Number(item.totamt || 0);
        return acc;
      },
      { qty: 0, basic: 0, amount: 0 },
    );
  }, [filteredData]);

  const totalQty = totals.qty;
  const totalBasicAmount = totals.basic.toFixed(2);
  const totalAmount = totals.amount.toFixed(2);

  /* rows shown in the table (dates pre-formatted, no total row) */
  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        totamt: Number(item.totamt || 0).toFixed(2),
        BillDate: item?.BillDate
          ? dayjs(item.BillDate).format("DD/MMM/YYYY")
          : "",
      })),
    [filteredData],
  );

  /* export data = rows + total row */
  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      billno: "",
      BillDate: "",
      CustName: "",
      PhoneNo: "",
      TOTQTY: totalQty,
      TOTBASICAMT: totalBasicAmount,
      GST: "",
      totamt: totalAmount,
    },
  ];

  return (
    <div className={styles.page}>
      <InventoryPageHeader
        title="Return Register"
        subtitle="Point of Sale · Return Register"
        icon={<RollbackOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={filteredData.length}
        visible={filteredData.length > 0}
        stats={[
          {
            label: "Records",
            value: filteredData.length,
            color: "blue",
            decimals: 0,
          },
          { label: "Qty", value: totalQty, color: "orange", decimals: 0 },
          {
            label: "Basic Amt",
            value: totals.basic,
            color: "amber",
            decimals: 2,
          },
          {
            label: "Total Amt",
            value: totals.amount,
            color: "green",
            decimals: 2,
          },
        ]}
      />

      <div className={styles.toolbar} style={{ flexWrap: "wrap", rowGap: 12 }}>
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", rowGap: 12 }}
        >
          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <CalendarOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>Start Date</div>
              <DatePicker
                className="mainProductPicker"
                style={{ width: "100%" }}
                size="middle"
                value={dates[0]}
                onChange={(d) => setDates([d || dayjs(), dates[1]])}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </div>
          </div>

          <div className="mainProductHighlight">
            <span className="mainProductIcon">
              <CalendarOutlined />
            </span>
            <div className="mainProductInner">
              <div className={styles.toolbarLabel}>End Date</div>
              <DatePicker
                className="mainProductPicker"
                style={{ width: "100%" }}
                size="middle"
                value={dates[1]}
                onChange={(d) => setDates([dates[0], d || dayjs()])}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </div>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="ReturnRegister"
            totals={{ totalBasicAmount, totalAmount }}
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="key"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No return records found for the selected dates"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="center">{totalQty}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totalBasicAmount}
            </Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">{totalAmount}</Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      <style>{`
  @keyframes mainProductPulse {
    0%   { box-shadow: 0 0 0 0 rgba(24,144,255,0.30); }
    70%  { box-shadow: 0 0 0 8px rgba(24,144,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(24,144,255,0); }
  }
  .mainProductHighlight {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 18px 8px 10px;
    border-radius: 14px;
    background: #eef6ff;
    border: 1.5px solid #65aefd;
    box-shadow: 0 0 0 1.5px #bcdcff inset;
    animation: mainProductPulse 2.4s infinite;
    min-width: 210px;
    flex-shrink: 0;
  }
  .mainProductIcon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(24,144,255,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #1677ff;
    flex-shrink: 0;
  }
  .mainProductInner {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
    min-width: 0;
  }
  .mainProductPicker.ant-picker {
    height: 34px !important;
    border-radius: 8px !important;
    background: #ffffff !important;
    border: 1px solid #d6e8ff !important;
    width: 100%;
  }
  .mainProductPicker.ant-picker:hover {
    border-color: #65aefd !important;
  }
  .mainProductPicker.ant-picker-focused {
    border-color: #1890ff !important;
    box-shadow: none !important;
  }
  .mainProductPicker .ant-picker-input > input {
    font-size: 13px;
    font-weight: 600;
    color: #1f2430;
  }
  .ant-table-thead > tr > th {
    white-space: nowrap !important;
  }
`}</style>
    </div>
  );
};

export default ReturnRegister;